// @flow

import config from 'config/app.config';
import { CountryCodes, Licenses } from 'types/common';

import type { FranceConnectState, UserType } from '../store/types';

const hasClickedDepositLater = sessionStorage.getItem('hasClickedDepositLater');

const shouldRedirectStep1 = (user: UserType): boolean =>
  !(user.email || (user.international_phone_number && user.phone_verified));
const shouldRedirectStep2 = (
  user: UserType,
  franceConnect: FranceConnectState,
  userHasScannedId: boolean,
): boolean => {
  switch (config.license) {
    case Licenses.ES:
      return (
        !(
          user.gender
          && user.first_name
          && user.last_name
          && user.birth_date
          && user.address1
          && user.zip_code
          && user.city
          && user.country
        )
        && !(franceConnect && franceConnect.user)
        && !userHasScannedId
      );
    case Licenses.DE:
      return false;
    case Licenses.FR:
    default:
      return (
        !(
          user.gender
          && user.first_name
          && user.last_name
          && user.birth_date
          && user.birth_country
          && user.address1
          && user.zip_code
          && user.city
          && user.country
        )
        && !(franceConnect && franceConnect.user)
        && !userHasScannedId
      );
  }
};
const shouldRedirectStep2b = (user: UserType, franceConnect: FranceConnectState): boolean => {
  return (
    (franceConnect
      && franceConnect.user
      && !user.is_real_nickname
      && !user.proof_of_address_uploaded)
    || !(
      user.gender
      && user.first_name
      && user.last_name
      && user.birth_date
      && user.address1
      && user.zip_code
      && user.city
      && user.country
      && user.has_accepted_cgu
    )
  );
};
const shouldRedirectStep2c = (user: UserType): boolean => {
  switch (config.license) {
    case Licenses.ES:
      if (user.country === CountryCodes.ES) {
        return false;
      } else {
        return !user.is_real_nickname && !user.proof_of_address_uploaded;
      }
    case Licenses.DE:
      return false;
    case Licenses.FR:
    default:
      return !user.is_real_nickname && !user.proof_of_address_uploaded;
  }
};
const shouldRedirectStep3 = (user: UserType): boolean => {
  switch (config.license) {
    case Licenses.ES:
      if (user.country === CountryCodes.ES) {
        return !!(!user.is_real_nickname && user.proof_of_address_uploaded);
      } else {
        return !user.is_real_nickname;
      }
    case Licenses.DE:
      return !user.is_real_nickname;
    case Licenses.FR:
    default:
      return !!(!user.is_real_nickname && user.proof_of_address_uploaded);
  }
};
const shouldRedirectStep4 = (user: UserType): boolean => {
  switch (config.license) {
    case Licenses.ES:
      return false;
    case Licenses.FR:
      return false;
    case Licenses.DE:
      return !!(user.is_real_nickname && !user.max_deposit_per_month);
    default:
      return !!(user.is_real_nickname && !user.max_deposit_per_month);
  }
};

const shouldRedirectStep4d = (user: UserType): boolean => {
  switch (config.license) {
    case Licenses.ES:
      return false;
    case Licenses.DE:
      return false;
    case Licenses.FR:
    default:
      return !!(
        user.is_real_nickname
        && !user.max_deposit_per_week
      );
  }
};

const shouldRedirectStep11 = (user: UserType): boolean => {
  switch (config.license) {
    case Licenses.FR:
      return !!(
        user.max_deposit_per_week
        && user.withdrawal_auto_above
      );
    case Licenses.ES:
    case Licenses.DE:
    default:
      return false;
  }
};

const shouldRedirectStep5 = (user: UserType): boolean => {
  switch (config.license) {
    case Licenses.ES:
      return !!(
        !user.first_deposit
        && !hasClickedDepositLater
        && !user.id_checked
        && !user.withdrawal_auto_above
        && user.country_code === CountryCodes.ES
      );
    case Licenses.DE:
      return false;
    case Licenses.FR:
      return false;
    default:
      return !!(
        user.max_deposit_per_week
        && !user.first_deposit
        && !hasClickedDepositLater
        && !user.id_checked
        && !user.withdrawal_auto_above
      );
  }
};
const shouldRedirectStep7 = (user: UserType): boolean => {
  switch (config.license) {
    case Licenses.ES:
      return !!(!user.id_checked && !user.withdrawal_auto_above);
    case Licenses.DE:
      return !!(user.max_deposit_per_month && !user.id_checked && !user.withdrawal_auto_above);
    case Licenses.FR:
      return false;
    default:
      return !!(
        user.max_deposit_per_week
        && !user.id_checked
        && !user.withdrawal_auto_above
        && (!!user.first_deposit || hasClickedDepositLater)
      );
  }
};
const shouldRedirectStep8 = (user: UserType): boolean =>
  !!(!user.address_verified && user.id_checked && !user.withdrawal_auto_above);
const shouldRedirectStep9 = (user: UserType): boolean =>
  !!(user.address_verified && !user.withdrawal_auto_above);
const shouldRedirectStep10 = (user: UserType): boolean => !!user.withdrawal_auto_above;

export const stepToRedirectTo = (
  user: UserType,
  franceConnect: FranceConnectState,
  userHasScannedId: boolean,
): ?string => {
  if (shouldRedirectStep1(user)) {
    return '/step1';
  } else if (shouldRedirectStep2(user, franceConnect, userHasScannedId)) {
    return '/step2';
  } else if (shouldRedirectStep2b(user, franceConnect)) {
    return '/step2/2';
  } else if (shouldRedirectStep2c(user)) {
    return '/step2/3';
  } else if (shouldRedirectStep3(user)) {
    return '/step3';
  } else if (shouldRedirectStep4(user)) {
    return '/step4';
  } else if (shouldRedirectStep4d(user)) {
    return '/step4/4';
  } else if (shouldRedirectStep11(user)) { // special case for Welcome Bonus (move this down when the OP is no longer available). This only affects the FR license
    // as the condition for the other licenses is always false
    return '/step11';
  } else if (shouldRedirectStep5(user)) {
    return '/step5';
  } else if (shouldRedirectStep7(user)) {
    return '/step7';
  } else if (shouldRedirectStep8(user)) {
    return '/step10'; // api not ready
  } else if (shouldRedirectStep9(user)) {
    return '/step10'; // api not ready
  } else if (shouldRedirectStep10(user)) {
    return '/step10';
  }
};
